import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FiPlay, FiPause, FiVolume2, FiMaximize, FiTool } from 'react-icons/fi';

const VideoContainer = styled.section`
  background: ${props => props.theme.colors.divider};
  padding: 4rem 0;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const VideoContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const VideoTitle = styled.h2`
  color: ${props => props.theme.colors.heading};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const VideoSubtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const VideoPlayer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  background: black;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const VideoElement = styled.video`
  width: 100%;
  height: auto;
  display: block;
  cursor: pointer;
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background: linear-gradient(135deg, ${props => props.theme.colors.primaryDark}, ${props => props.theme.colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const VideoThumbnail = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primaryDark}, ${props => props.theme.colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8rem;
  color: white;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 4rem;
  }
`;

const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;

  &:hover {
    background: white;
    transform: translate(-50%, -50%) scale(1.1);
  }

  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin-left: 4px;
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;

    svg {
      font-size: 1.5rem;
    }
  }
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${VideoPlayer}:hover & {
    opacity: 1;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  position: relative;
  cursor: pointer;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.theme.colors.primary};
  border-radius: 2px;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const VideoDescription = styled.div`
  margin-top: 2rem;
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  line-height: 1.6;
`;

const VideoPlayerComponent = ({ 
  videoSrc, 
  title, 
  subtitle, 
  description, 
  showControls = true,
  autoPlay = false,
  muted = false 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  const handlePlayPause = () => {
    if (showPlaceholder) {
      // First click - hide placeholder and show video
      setShowPlaceholder(false);
      setIsPlaying(true);
      // Use setTimeout to ensure the video element is rendered before trying to play
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(error => {
            console.error('Error playing video:', error);
          });
        }
      }, 100);
    } else if (videoRef.current) {
      // Subsequent clicks - play/pause the video
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoClick = () => {
    handlePlayPause();
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = (clickX / width) * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <VideoContainer>
      <VideoContent>
        {title && <VideoTitle>{title}</VideoTitle>}
        {subtitle && <VideoSubtitle>{subtitle}</VideoSubtitle>}

        <VideoPlayer>
          {showPlaceholder ? (
            <VideoPlaceholder onClick={handlePlayPause}>
              <VideoThumbnail>
                <FiTool size={80} />
              </VideoThumbnail>
              <PlayButton onClick={handlePlayPause}>
                <FiPlay />
              </PlayButton>
            </VideoPlaceholder>
          ) : (
            <VideoElement
              ref={videoRef}
              src={videoSrc}
              onClick={handleVideoClick}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnded}
              onError={(e) => {
                console.error('Video error:', e);
                setVideoError(true);
                setShowPlaceholder(true);
              }}
              onLoadStart={() => console.log('Video loading started:', videoSrc)}
              onCanPlay={() => console.log('Video can play:', videoSrc)}
              autoPlay={autoPlay}
              muted={muted}
              controls={false}
              preload="metadata"
            />
          )}

          {showControls && (
            <VideoControls>
              <ControlButton onClick={handlePlayPause}>
                {isPlaying ? <FiPause /> : <FiPlay />}
              </ControlButton>
              
              <ProgressBar onClick={handleProgressClick}>
                <ProgressFill progress={progress} />
              </ProgressBar>
              
              <ControlButton onClick={handleVolumeToggle}>
                <FiVolume2 />
              </ControlButton>
              
              <ControlButton onClick={handleFullscreen}>
                <FiMaximize />
              </ControlButton>
            </VideoControls>
          )}
        </VideoPlayer>

        {description && (
          <VideoDescription>
            <p>{description}</p>
          </VideoDescription>
        )}
      </VideoContent>
    </VideoContainer>
  );
};

export default VideoPlayerComponent; 